// ── Where a field ticket's hours come from, and what the ticket knows ──
//
// The Time Card Approval view used to build every ticket off HEET
// (readHEETLabor). HEET posts a day or two behind the field, and even once it
// has a day its pull misses anything keyed on the card afterwards: on 9/21/26
// job 54026071 HEET held 82 people / 817 hrs, no per diem / truck / rig pay at
// all and one 0-hour truck, while the HeavyJob cards held 83 / 821.5 hrs, 86
// adders and 29 units. The localserver ticket scripts moved to the live
// HeavyJob API on 9/21 ("the card wins"); this module is the same basis for
// the web tickets, plus the job rules those scripts learned the hard way.
//
// Everything here is pure — the view feeds it the two responses and the
// roster, and reads back rows in HEET's shape (so ticketFrom, the QA and the
// exports take them unchanged) and a `feed` record of what was used and why.

const HOURS = new Set(['Regular', 'Overtime']);
const ADDERS = ['Per Diem', 'Truck Pay', 'Rig Pay'];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r2 = (n) => Math.round(num(n) * 100) / 100;

// ── Job rules ──
// What the VoltaGrid packets do that the raw cards don't say. Kept here, in
// one visible table, rather than scattered through the view.
//   fold:   phase codes billed on another code. 100105 Other Project Support
//           is billed on 100101 Project Team (the client's WBS has no 100105).
//   people: name / classification a person bills under regardless of card,
//           roster or workbook. Hirshell Guynes goes by Jance on the client's
//           paper and bills as HSE Specialist (settled with the client on
//           the WE 8/30 Rev 1 tickets).
//   clsJob: whose attendance master supplies the billing classification.
//           54026071 prints 54026070's rate-sheet names — most of the crew is
//           on both jobs the same week and 70's tickets are already with the
//           client, so the two must read alike.
//   sister: the other job the same crew cards to (split-day check).
const FOLD_VG = { 100105: { code: '100101', desc: 'Project Team' } };
const PEOPLE_VG = { 106529: { name: 'Jance Guynes', cls: 'HSE Specialist', why: 'goes by Jance; bills as HSE Specialist' } };
export const TICKET_RULES = {
    54026070: { fold: FOLD_VG, people: PEOPLE_VG, sister: '54026071' },
    54026071: { fold: FOLD_VG, people: PEOPLE_VG, clsJob: '54026070', sister: '54026070' },
};
export const rulesFor = (job) => TICKET_RULES[String(job)] || { fold: {}, people: {} };

// ── Job name ──
// JDE and HeavyJob both cut a job description at 30 characters ("Block 4
// (Mechanical, Structura"). The job's SharePoint subsite carries the full
// name as "<job> - <name>"; use it when it extends the short one.
export const fullJobName = (short, siteTitle, job) => {
    const s = String(short || '').trim();
    const site = String(siteTitle || '').trim()
        .replace(new RegExp(`^${String(job || '').replace(/[^0-9A-Za-z]/g, '')}\\s*[-–—:]*\\s*`), '')
        .trim();
    if (!site) return s;
    if (!s) return site;
    const norm = (x) => x.toLowerCase().replace(/[^a-z0-9]/g, '');
    return norm(site).startsWith(norm(s)) && site.length > s.length ? site : s;
};

// ── Merging the two feeds ──

/** Card hours, HEET hours, adders and equipment per person — for the checks. */
const tally = (rows) => {
    const people = {};
    const equip = {};
    (rows || []).forEach((r) => {
        if (r.Record_Type === 'Equipment') {
            const k = String(r.Equipment_Code || r.Equipment_Code_Description || '').trim();
            if (!k) return;
            equip[k] = equip[k] || { unit: k, desc: r.Equipment_Code_Description || '', hours: 0 };
            equip[k].hours += num(r.Hours);
            return;
        }
        const id = String(r.Staff_ID || '').trim();
        if (!id) return;
        const p = people[id] || (people[id] = { id, name: r.Staff_Name || '', hours: 0, adders: new Set(), raw: false });
        if (!p.name && r.Staff_Name) p.name = r.Staff_Name;
        if (HOURS.has(r.Record_Type)) p.hours += num(r.Hours);
        if (ADDERS.includes(r.Record_Type)) p.adders.add(r.Record_Type);
        if (r.Hour_Tag_Code === 'HJRAW') p.raw = true;
    });
    return { people, equip };
};

/**
 * Card vs HEET for one day — who HEET is missing, who it has that the card
 * no longer does, hour differences, adders the card carries that HEET lacks,
 * and the equipment each side holds. Only meaningful when both answered.
 */
export const compareFeeds = (cardRows, heetRows) => {
    const c = tally(cardRows);
    const h = tally((heetRows || []).filter((r) => r.Hour_Tag_Code !== 'HJRAW'));
    const onlyCard = [];
    const onlyHeet = [];
    const hours = [];
    const adders = [];
    Object.values(c.people).forEach((p) => {
        if (p.hours <= 0.005) return;
        const q = h.people[p.id];
        if (!q || q.hours <= 0.005) { onlyCard.push({ id: p.id, name: p.name, hours: r2(p.hours) }); return; }
        if (Math.abs(p.hours - q.hours) > 0.05) hours.push({ id: p.id, name: p.name, card: r2(p.hours), heet: r2(q.hours) });
        const missing = [...p.adders].filter((a) => !q.adders.has(a));
        if (missing.length) adders.push({ id: p.id, name: p.name, missing });
    });
    Object.values(h.people).forEach((q) => {
        if (q.hours <= 0.005) return;
        if (!c.people[q.id] || c.people[q.id].hours <= 0.005) onlyHeet.push({ id: q.id, name: q.name, hours: r2(q.hours) });
    });
    const sum = (o) => Object.values(o).reduce((a, x) => a + x.hours, 0);
    const heads = (o) => Object.values(o).filter((x) => x.hours > 0.005).length;
    const adderCount = (o) => Object.values(o).reduce((a, x) => a + x.adders.size, 0);
    return {
        card: { people: heads(c.people), hours: r2(sum(c.people)), adders: adderCount(c.people), units: Object.values(c.equip).filter((e) => e.hours > 0).length, equipHours: r2(sum(c.equip)) },
        heet: { people: heads(h.people), hours: r2(sum(h.people)), adders: adderCount(h.people), units: Object.values(h.equip).filter((e) => e.hours > 0).length, equipHours: r2(sum(h.equip)) },
        onlyCard,
        onlyHeet,
        hours,
        adders,
    };
};

/**
 * Whether the day's cards say anything about shift. Through 9/20/26 the
 * 54026070 office keyed ONE roll-up card a day (Lynnette's, shift 2) holding
 * both crews — its shift is noise. From 9/21 each foreman keys their own
 * card, and a per-foreman card's shift is the crew's. Trusted when at least
 * two cards carry people and none holds more than 60% of the day's crew.
 */
export const cardShiftTrust = (cards) => {
    const withPeople = (cards || []).filter((c) => num(c.people) > 0);
    const total = withPeople.reduce((a, c) => a + num(c.people), 0);
    if (!withPeople.length || !total) return { trusted: false, reason: 'no cards with people' };
    const big = withPeople.reduce((a, c) => (num(c.people) > num(a.people) ? c : a), withPeople[0]);
    const share = num(big.people) / total;
    const shifts = [...new Set(withPeople.map((c) => Number(c.shift) || 1))];
    if (withPeople.length < 2 || share > 0.6) {
        return {
            trusted: false,
            reason: `one roll-up card (${big.foreman || 'office'}) holds ${big.people} of ${total} people — its shift ${Number(big.shift) === 2 ? '2' : '1'} is not the crew's`,
        };
    }
    return {
        trusted: true,
        reason: `${withPeople.length} foreman cards, ${shifts.length === 1 ? (shifts[0] === 2 ? 'all night shift' : 'all day shift') : 'day and night'}`,
    };
};

/** staffId -> 'Day' | 'Night' off the card each person's hours are on. */
export const cardShiftById = (rows) => {
    const seen = {};
    (rows || []).forEach((r) => {
        if (!HOURS.has(r.Record_Type) || num(r.Hours) <= 0) return;
        const id = String(r.Staff_ID || '');
        if (!id) return;
        const s = Number(r.Shift) === 2 ? 'Night' : 'Day';
        seen[id] = seen[id] && seen[id] !== s ? 'both' : s;
    });
    return Object.fromEntries(Object.entries(seen).filter(([, s]) => s !== 'both'));
};

/**
 * One day's rows off whichever feed should answer. The cards answer whenever
 * the API returned time for the day; HEET answers when the API could not be
 * read (not configured, down) or holds nothing for the day (days before the
 * crew keyed cards, or a card since deleted). A per diem row keeps HEET's
 * paid amount for the on-screen estimate — the card has no money on it.
 */
export const mergeDayFeeds = ({ date, heet, hj }) => {
    const heetOk = Boolean(heet && !heet.error);
    const hjOk = Boolean(hj && !hj.error && hj.configured !== false);
    const hjRows = hjOk ? (hj.data || []).filter((r) => !date || r.Work_Date === date) : [];
    const hjCards = hjOk ? (hj.cards || []).filter((c) => !date || c.date === date) : [];
    const hasCardTime = hjRows.some((r) => num(r.Hours) > 0);
    const heetRows = heetOk ? (heet.data || []) : null;
    const basis = hasCardTime ? 'cards' : heetRows ? 'heet' : hjOk ? 'cards' : null;
    const errors = {
        heet: heetOk ? null : String(heet?.error || 'no response'),
        hj: hjOk ? null : String(hj?.error || (hj && hj.configured === false ? 'HeavyJob API not configured' : 'no response')),
    };
    if (!basis) return { error: `neither HeavyJob (${errors.hj}) nor HEET (${errors.heet}) answered` };

    let rows;
    if (basis === 'cards') {
        const paid = {};
        (heetRows || []).forEach((r) => {
            if (r.Record_Type === 'Per Diem' && num(r.Amount)) paid[String(r.Staff_ID)] = (paid[String(r.Staff_ID)] || 0) + num(r.Amount);
        });
        const used = new Set();
        rows = hjRows.map((r) => {
            if (r.Record_Type !== 'Per Diem') return r;
            const id = String(r.Staff_ID);
            if (!paid[id] || used.has(id)) return r;
            used.add(id);
            return { ...r, Amount: paid[id] };
        });
    } else {
        rows = heetRows || [];
    }
    return {
        rows,
        job: heet?.job || (hj?.jobDescription ? { Job_Description: hj.jobDescription, Client_Name: '' } : null),
        feed: {
            basis,
            date,
            cards: hjCards,
            fetchedAt: hj?.fetchedAt || null,
            trust: cardShiftTrust(hjCards),
            cardShift: cardShiftById(hjRows),
            compare: hasCardTime && heetRows ? compareFeeds(hjRows, heetRows) : null,
            adjustments: hjOk ? (hj.adjustments || []).filter((a) => !date || a.date === date) : [],
            errors,
            heetHasDay: Boolean(heetRows && heetRows.length),
        },
    };
};

// ── Applying the job rules to rows ──

/** 100105 → 100101 (and whatever else the job folds), labor and equipment. */
export const foldRows = (rows, fold) => {
    const map = fold || {};
    if (!Object.keys(map).length) return { rows: rows || [], folded: 0, people: [] };
    let folded = 0;
    const who = new Set();
    const out = (rows || []).map((r) => {
        const to = map[String(r.Cost_Code || '').trim()];
        if (!to) return r;
        if (HOURS.has(r.Record_Type) || r.Record_Type === 'Equipment') folded += num(r.Hours);
        if (r.Staff_ID && HOURS.has(r.Record_Type)) who.add(String(r.Staff_Name || r.Staff_ID));
        return { ...r, Cost_Code: to.code, Cost_Code_Description: to.desc, Folded_From: r.Cost_Code };
    });
    return { rows: out, folded: r2(folded), people: [...who] };
};

/** Per-person name overrides onto the rows (the class goes through clsFor). */
export const applyPeople = (rows, people) => {
    const map = people || {};
    if (!Object.keys(map).length) return rows || [];
    return (rows || []).map((r) => {
        const o = map[String(r.Staff_ID || '')];
        return o && o.name && r.Staff_Name !== o.name ? { ...r, Staff_Name: o.name, Card_Name: r.Staff_Name } : r;
    });
};

// ── Context checks ──
// What the ticket scripts print to the console before a packet goes out,
// as data the preview, the QA panel and the assistant all read.

const PRESENT = /^(x|l|eo)$/i;

/**
 * Attendance marks vs the cards for one date: people the master marks
 * present with no card time (on this job or the sister job), and people
 * with card time the master marks absent / time off / not at all.
 */
export const attendanceChecks = ({ roster, date, job, cardIds, sisterIds }) => {
    const rows = (roster?.roster || []);
    const hasMarks = rows.some((r) => r.marks && Object.keys(r.marks).length);
    if (!hasMarks || !date) return { available: false, presentNoCard: [], cardNotPresent: [] };
    const covered = rows.some((r) => r.marks && r.marks[date] !== undefined);
    if (!covered) return { available: false, presentNoCard: [], cardNotPresent: [] };
    const ids = new Set((cardIds || []).map(String));
    const other = new Set((sisterIds || []).map(String));
    const byId = Object.fromEntries(rows.map((r) => [String(r.id), r]));
    const presentNoCard = rows
        .filter((r) => (!r.job || String(r.job) === String(job)) && PRESENT.test(String(r.marks?.[date] || '')))
        .filter((r) => !ids.has(String(r.id)) && !other.has(String(r.id)))
        .map((r) => ({ id: String(r.id), name: r.name, mark: r.marks[date] }));
    const cardNotPresent = [...ids]
        .map((id) => ({ id, r: byId[id] }))
        .filter(({ r }) => !r || !PRESENT.test(String(r.marks?.[date] || '')))
        .map(({ id, r }) => ({ id, name: r?.name || '', mark: r ? (r.marks?.[date] || 'blank') : 'not on the master' }));
    return { available: true, presentNoCard, cardNotPresent };
};

/** People whose hours across this job and the sister job top 12 in a day. */
export const splitDayChecks = (rows, sisterRows, limit = 12) => {
    const hrs = (list) => {
        const m = {};
        (list || []).forEach((r) => {
            if (!HOURS.has(r.Record_Type)) return;
            const id = String(r.Staff_ID || '');
            if (!id) return;
            m[id] = m[id] || { hours: 0, name: r.Staff_Name || '' };
            m[id].hours += num(r.Hours);
        });
        return m;
    };
    const here = hrs(rows);
    const there = hrs(sisterRows);
    return Object.entries(here)
        .filter(([id, x]) => there[id] && x.hours + there[id].hours > limit + 0.005)
        .map(([id, x]) => ({ id, name: x.name, here: r2(x.hours), other: r2(there[id].hours), total: r2(x.hours + there[id].hours) }))
        .sort((a, b) => b.total - a.total);
};

/**
 * Roster Days/Nights vs a trusted card shift. The master's column is today's
 * assignment, not the date's — after a crew moves shift the old label stays
 * until someone edits it (26 of 54026071's crew still read Nights on 9/22 on
 * their first day-shift week). Where the cards are trusted, the card shift
 * places them and this names who the master disagrees with.
 */
export const staleRosterShift = (rosterShift, cardShift) => Object.entries(cardShift || {})
    .filter(([id, s]) => rosterShift?.[id] && rosterShift[id] !== s)
    .map(([id, s]) => ({ id, roster: rosterShift[id], card: s }));

/** One-line human summary of a day's feed, for the preview's source line. */
export const describeFeed = (feed) => {
    if (!feed) return '';
    const cards = feed.cards || [];
    const approved = cards.filter((c) => c.isApproved).length;
    const newest = cards.reduce((a, c) => (c.lastModified && (!a || c.lastModified > a) ? c.lastModified : a), null);
    if (feed.basis === 'cards') {
        return `HeavyJob cards, live — ${cards.length} card${cards.length === 1 ? '' : 's'}`
            + `${cards.length ? `, ${approved ? `${approved} approved` : 'none approved yet'}` : ''}`
            + `${newest ? `, last edited ${newest}` : ''}`;
    }
    return `HEET${feed.errors?.hj ? ` (HeavyJob API: ${feed.errors.hj})` : ' (no HeavyJob cards for this day)'}`;
};

// ── The day's context, as notes ──
// Everything above folded into short lines — what the preview shows under
// the ticket, and what the assistant is handed as ground truth. Levels:
// 'fail' (don't send as is), 'warn' (look before sending), 'info' (how the
// ticket was built).

const names = (list, n = 6) => {
    const shown = list.slice(0, n).map((x) => x.name || x.id).join(', ');
    return list.length > n ? `${shown} and ${list.length - n} more` : shown;
};
const fmtStamp = (iso) => {
    const t = Date.parse(iso || '');
    if (!Number.isFinite(t)) return '';
    return new Date(t).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export const contextNotes = ({
    feed, prior, fold, attendance, stale, splitDays, dropped, overrides, clsJob, clsJobFound, sister,
} = {}) => {
    const out = [];
    const add = (level, area, text) => out.push({ level, area, text });
    if (feed) {
        const cards = feed.cards || [];
        const approved = cards.filter((c) => c.isApproved).length;
        const newest = cards.reduce((a, c) => (c.lastModified && (!a || c.lastModified > a) ? c.lastModified : a), null);
        if (feed.basis === 'cards') {
            add('info', 'hours', `Hours, adders and equipment from the live HeavyJob cards — ${cards.length} card${cards.length === 1 ? '' : 's'}`
                + `${approved ? `, ${approved} approved` : ', none approved yet'}${newest ? `, last edited ${fmtStamp(newest)}` : ''}.`);
            if (cards.length && !approved) add('warn', 'hours', 'No card for this day is approved in HeavyJob yet — foremen can still change it; rebuild right before sending.');
        } else {
            add(feed.errors?.hj ? 'warn' : 'info', 'hours', feed.errors?.hj
                ? `HeavyJob API unavailable (${feed.errors.hj}) — this ticket is on HEET, which runs a day or two behind the cards and misses adders keyed late.`
                : 'No HeavyJob cards carry time on this day — the ticket is on HEET / actual cost.');
        }
        const c = feed.compare;
        if (c) {
            const behind = c.onlyCard.length || c.hours.length || c.adders.length || c.onlyHeet.length;
            if (!feed.heetHasDay) {
                add('info', 'hours', 'HEET has not pulled this day yet — nothing to compare the cards against.');
            } else if (!behind && Math.abs(c.card.equipHours - c.heet.equipHours) < 0.05) {
                add('info', 'hours', `HEET agrees with the cards (${c.card.people} people / ${c.card.hours} hrs).`);
            } else {
                add('info', 'hours', `HEET holds ${c.heet.people} people / ${c.heet.hours} hrs, ${c.heet.adders} adders, ${c.heet.units} units; `
                    + `the cards ${c.card.people} / ${c.card.hours} hrs, ${c.card.adders} adders, ${c.card.units} units. The cards win.`);
                if (c.onlyCard.length) add('info', 'hours', `On the cards, not yet in HEET: ${names(c.onlyCard.map((x) => ({ name: `${x.name} (${x.hours})` })))}.`);
                if (c.onlyHeet.length) add('warn', 'hours', `In HEET but no longer on any card (card wins — off the ticket): ${names(c.onlyHeet.map((x) => ({ name: `${x.name} (${x.hours})` })))}.`);
                if (c.hours.length) add('info', 'hours', `Re-keyed since HEET pulled: ${names(c.hours.map((x) => ({ name: `${x.name} ${x.heet}→${x.card}` })))}.`);
                if (c.adders.length) add('info', 'adders', `${c.adders.length} ${c.adders.length === 1 ? 'person has' : 'people have'} PD/TP/RR on the card that HEET lacks.`);
            }
        }
        if ((feed.adjustments || []).length) {
            add('warn', 'adders', `Card adjustments that are not PD / TP / RR (not printed): ${names(feed.adjustments.map((a) => ({ name: `${a.name} — ${a.desc || a.code}` })))}.`);
        }
        if (feed.trust) {
            add('info', 'shift', feed.trust.trusted
                ? `Shift from the foreman cards (${feed.trust.reason}), above the attendance master.`
                : `Card shift ignored: ${feed.trust.reason}. Shift from the reviewed card / attendance master; anyone neither places is on days.`);
        }
    }
    if (prior) {
        add('warn', 'shift', `No reviewed time card for this week, and the attendance master was saved ${prior.saved}, after it — `
            + `its Days/Nights is that later state. Shift from the WE ${prior.we} reviewed card where it names the person (${prior.night} on nights there); check against this date's sign-in sheets.`);
    }
    if (stale && stale.length) {
        add('warn', 'shift', `${stale.length} ${stale.length === 1 ? 'person' : 'people'} the attendance master lists on a different shift than their foreman card `
            + `(card placed them): ${names(stale.map((x) => ({ name: `${x.name || x.id} ${x.roster}→${x.card}` })))}. The master's Days/Nights column is today's assignment — update it if they moved.`);
    }
    if (fold && fold.folded > 0) {
        add('info', 'phase', `${fold.folded} hrs on 100105 Other Project Support billed on 100101 Project Team (${names(fold.people.map((n) => ({ name: n })), 4)}).`);
    }
    if (overrides && overrides.length) {
        add('info', 'class', `Job rule: ${overrides.map((o) => `${o.card || o.id} prints as ${o.name}${o.cls ? `, ${o.cls}` : ''}`).join('; ')}.`);
    }
    if (clsJob) {
        add(clsJobFound ? 'info' : 'warn', 'class', clsJobFound
            ? `Billing classifications from ${clsJob}'s attendance master, so both jobs' tickets read alike.`
            : `Could not read ${clsJob}'s attendance master — classifications fall back to this job's own sources.`);
    }
    if (attendance?.available) {
        if (attendance.presentNoCard.length) {
            add('warn', 'people', `Marked present on the attendance master with no card time on this job${sister ? ` or ${sister}` : ''}: ${names(attendance.presentNoCard)}.`);
        }
        const odd = attendance.cardNotPresent.filter((x) => x.mark !== 'not on the master');
        const missing = attendance.cardNotPresent.filter((x) => x.mark === 'not on the master');
        if (odd.length) add('warn', 'people', `On a card but the master marks them ${[...new Set(odd.map((x) => `"${x.mark}"`))].join(' / ')}: ${names(odd)}.`);
        if (missing.length) add('warn', 'people', `On a card but not on the attendance master at all: ${names(missing)}.`);
    }
    if (splitDays && splitDays.length) {
        add('warn', 'hours', `Over 12 hrs across this job and ${sister || 'the sister job'}: ${names(splitDays.map((x) => ({ name: `${x.name} ${x.here}+${x.other}=${x.total}` })))}.`);
    }
    if (dropped && dropped.length) {
        add('info', 'people', `Left off (adders, no hours): ${names(dropped)}.`);
    }
    return out;
};
